// ============================================================
// Makbuz / fatura belgesi ve online ödeme (§4)
//
// makbuzNo() zaten numara üretiyordu — "MK-2026-4821" gibi — ama o
// numaranın karşılığı olan bir belge yoktu. Veli elinde hiçbir şey
// olmadan ödeme yapıyordu.
//
// PDF üretimi için kütüphane eklenmedi: tarayıcının kendi yazdırma
// motoru (print → PDF olarak kaydet) hem her cihazda çalışıyor hem de
// paket boyutunu büyütmüyor. Belge HTML olarak üretilip yazdırma
// penceresinde açılıyor.
// ============================================================

import { BUGUN } from "../config.js";
import { B, subeBulHerYerde, subeninKiracisi } from "./context.js";
import { donemAdi } from "../lib/fmt.js";
import { feeFor } from "./fees.js";
import { faturaDurumu, faturaTutari, tahsilEdilen } from "./invoices.js";
import { veliler } from "./students.js";

const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

/**
 * Online ödeme sağlayıcıları.
 * `hazir: false` — entegrasyon kurulmadı; bağlantı üretilir ama
 * gerçek POS'a gitmez. Kurulunca yalnızca `link()` değişir.
 */
export const ODEME_SAGLAYICILARI = {
  iyzico:      { label: "iyzico", hazir: false, komisyon: 0.0289 },
  creditwest:  { label: "Creditwest Sanal POS", hazir: false, komisyon: 0.025 },
  vakiflar:    { label: "Kıbrıs Vakıflar Bankası", hazir: false, komisyon: 0.024 },
};

/** Belge türleri: tahsilat yapılmışsa makbuz, yapılmamışsa fatura. */
export const BELGE_TURU = { makbuz: "Tahsilat Makbuzu", fatura: "Ödeme Bildirimi" };

/**
 * Bir fatura için belge verisi üretir.
 * Tutar burada da hesaplanır, kayıttan okunmaz — okul ücreti
 * tarafındaki ilkenin aynısı.
 */
export function belgeVerisi(student, inv, branchId) {
  const t = sube(branchId);
  const kiraci = subeninKiracisi(t.id);
  const c = t.classes.find(x => x.id === student.classId);
  const fee = feeFor(student, inv);
  const tutar = faturaTutari(student, inv);
  const odenen = tahsilEdilen(inv);
  const durum = faturaDurumu(student, inv);
  const hareketler = inv.transactions ?? [];

  return {
    tur: hareketler.length ? "makbuz" : "fatura",
    no: hareketler.at(-1)?.receiptNo ?? `BLD-${inv.period}-${student.id.toUpperCase()}`,
    tarih: hareketler.at(-1)?.date ?? BUGUN,

    kurum: {
      ad: kiraci?.name ?? "",
      sube: t.name,
      adres: t.address,
      telefon: t.phone,
    },

    veli: veliler(student, t.id)[0] ?? { name: student.parent, phone: student.parentPhone },
    ogrenci: { ad: student.name, sinif: c?.name ?? "", program: student.schedule },

    donem: inv.period,
    donemAdi: donemAdi(inv.period),
    vade: inv.due,

    kalemler: [
      { ad: `${fee.half ? "Yarım gün" : "Tam gün"} eğitim ücreti — ${c?.name ?? ""}`, tutar: fee.base },
      ...(fee.service ? [{ ad: "Servis ücreti", tutar: fee.service }] : []),
      ...(fee.discount ? [{ ad: `${fee.discountLabel} (%${fee.pct})`, tutar: -fee.discount }] : []),
      ...(fee.override !== null ? [{ ad: `Özel tutar${inv.note ? ` — ${inv.note}` : ""}`, tutar: fee.override - fee.computed }] : []),
    ],

    tutar,
    odenen,
    kalan: tutar - odenen,
    durum,
    hareketler,
    not: inv.note ?? null,
  };
}

/**
 * Yazdırılabilir belge HTML'i.
 * Ayrı bir pencerede açılır; tarayıcının "PDF olarak kaydet" seçeneği
 * kütüphane eklemeden PDF üretir.
 */
export function belgeHTML(b) {
  const para = n => `${n.toLocaleString("tr-TR")} ₺`;
  const esc = s => String(s ?? "").replace(/[&<>"]/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

  return `<!DOCTYPE html>
<html lang="tr"><head><meta charset="UTF-8">
<title>${esc(BELGE_TURU[b.tur])} ${esc(b.no)}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, sans-serif; color: #2B2440; margin: 0; font-size: 12px; }
  .ust { display: flex; justify-content: space-between; align-items: flex-start;
         border-bottom: 3px solid #2B2440; padding-bottom: 12px; margin-bottom: 20px; }
  .kurum { font-size: 18px; font-weight: 800; }
  .alt { color: #5A5470; font-size: 11px; line-height: 1.5; }
  .tur { text-align: right; }
  .tur b { display: block; font-size: 15px; text-transform: uppercase; letter-spacing: .5px; }
  .kutular { display: flex; gap: 16px; margin-bottom: 20px; }
  .kutu { flex: 1; background: #F7F6FB; border-radius: 8px; padding: 12px; }
  .kutu h3 { margin: 0 0 6px; font-size: 10px; text-transform: uppercase;
             letter-spacing: .5px; color: #5A5470; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; color: #5A5470;
       border-bottom: 2px solid #E8E6F0; padding: 8px 0; }
  td { padding: 8px 0; border-bottom: 1px solid #E8E6F0; }
  td.sag, th.sag { text-align: right; }
  .toplam { display: flex; justify-content: flex-end; gap: 40px; font-size: 15px;
            font-weight: 800; padding: 10px 0; border-top: 2px solid #2B2440; }
  .durum { display: inline-block; padding: 3px 10px; border-radius: 999px;
           font-size: 10px; font-weight: 700; }
  .odendi { background: #E0F5F2; color: #0E9E8F; }
  .acik { background: #FDEAE7; color: #EE6C5A; }
  .dip { margin-top: 28px; padding-top: 12px; border-top: 1px solid #E8E6F0;
         color: #5A5470; font-size: 10px; line-height: 1.6; }
  @media print { .yazdir { display: none; } }
  .yazdir { position: fixed; top: 12px; right: 12px; padding: 8px 16px;
            background: #2B2440; color: #fff; border: 0; border-radius: 8px;
            font-weight: 600; cursor: pointer; }
</style></head>
<body>
  <button class="yazdir" onclick="window.print()">Yazdır / PDF kaydet</button>

  <div class="ust">
    <div>
      <div class="kurum">${esc(b.kurum.ad)}</div>
      <div class="alt">${esc(b.kurum.sube)}<br>${esc(b.kurum.adres)}<br>${esc(b.kurum.telefon)}</div>
    </div>
    <div class="tur">
      <b>${esc(BELGE_TURU[b.tur])}</b>
      <div class="alt">No: ${esc(b.no)}<br>Tarih: ${esc(b.tarih)}</div>
    </div>
  </div>

  <div class="kutular">
    <div class="kutu">
      <h3>Veli</h3>
      <div><b>${esc(b.veli.name)}</b></div>
      <div class="alt">${esc(b.veli.phone ?? "")}</div>
    </div>
    <div class="kutu">
      <h3>Öğrenci</h3>
      <div><b>${esc(b.ogrenci.ad)}</b></div>
      <div class="alt">${esc(b.ogrenci.sinif)} · ${b.ogrenci.program === "yarim" ? "Yarım gün" : "Tam gün"}</div>
    </div>
    <div class="kutu">
      <h3>Dönem</h3>
      <div><b>${esc(b.donemAdi)}</b></div>
      <div class="alt">Son ödeme: ${esc(b.vade)}</div>
    </div>
  </div>

  <table>
    <thead><tr><th>Açıklama</th><th class="sag">Tutar</th></tr></thead>
    <tbody>
      ${b.kalemler.map(k => `<tr><td>${esc(k.ad)}</td><td class="sag">${para(k.tutar)}</td></tr>`).join("")}
    </tbody>
  </table>

  <div class="toplam"><span>Toplam</span><span>${para(b.tutar)}</span></div>

  ${b.hareketler.length ? `
    <table style="margin-top:16px">
      <thead><tr><th>Tahsilat</th><th>Yöntem</th><th>Makbuz</th><th class="sag">Tutar</th></tr></thead>
      <tbody>
        ${b.hareketler.map(h => `<tr>
          <td>${esc(h.date)}</td><td>${esc(h.method)}</td>
          <td>${esc(h.receiptNo)}</td><td class="sag">${para(h.amount)}</td>
        </tr>`).join("")}
      </tbody>
    </table>` : ""}

  <div style="margin-top:12px">
    ${b.kalan > 0
      ? `<span class="durum acik">Açık bakiye: ${para(b.kalan)}</span>`
      : `<span class="durum odendi">Ödendi</span>`}
  </div>

  <div class="dip">
    ${b.not ? `${esc(b.not)}<br>` : ""}
    Bu belge ${esc(b.kurum.ad)} tarafından elektronik ortamda üretilmiştir.
    Tutar, sınıf ücret tarifesinden hesaplanmıştır.
    ${b.tur === "makbuz" ? "Tahsilat kaydı sistemde saklıdır." : "Ödeme sonrası makbuz düzenlenecektir."}
  </div>
</body></html>`;
}

// ---------- Online ödeme ----------

/**
 * Ödeme bağlantısı üretir.
 *
 * Sağlayıcı entegrasyonu kurulmadığı için bağlantı gerçek POS'a
 * gitmez; yapı hazır, `hazir: true` olduğunda yalnızca URL değişir.
 * Sahte bir "ödendi" akışı KURULMADI — para alındı sanmak, hiç
 * almamaktan kötüdür.
 */
export function odemeBaglantisi(student, inv, branchId, saglayici = "iyzico") {
  const s = ODEME_SAGLAYICILARI[saglayici];
  if (!s) return null;

  const t = sube(branchId);
  const tutar = faturaTutari(student, inv) - tahsilEdilen(inv);

  return {
    saglayici,
    label: s.label,
    hazir: s.hazir,
    tutar,
    komisyon: Math.round(tutar * s.komisyon),
    netTahsilat: tutar - Math.round(tutar * s.komisyon),
    // Gerçek entegrasyonda sağlayıcı token'lı URL döner.
    url: s.hazir
      ? `https://odeme.example/${saglayici}/${t.id}/${student.id}/${inv.period}`
      : null,
    referans: `${t.id.toUpperCase()}-${student.id.toUpperCase()}-${inv.period}`,
  };
}

/** Şubede online ödeme açık mı? */
export const onlineOdemeAcik = branchId =>
  Object.values(ODEME_SAGLAYICILARI).some(s => s.hazir) && Boolean(sube(branchId));
